import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { LogOut } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AsyncListShell } from "@/components/admin/AsyncListShell";
import { LeadPeriodControls, LEAD_FILTER_INITIAL } from "@/components/admin/LeadPeriodFilter";
import { AdvisorClientDetail, autoLabel } from "@/components/asesor/AdvisorClientDetail";
import { formatIngreso, inPeriod, periodRange } from "@/lib/leadPeriod";
import { formatApiError } from "@/lib/api";
import { advisorApi, advisorToken } from "@/lib/advisorApi";

export default function AdvisorPortal() {
  const nav = useNavigate();
  const { clienteId } = useParams();
  const [me, setMe] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lf, setLf] = useState(LEAD_FILTER_INITIAL);

  useEffect(() => {
    Promise.all([advisorApi.get("/asesor/auth/me"), advisorApi.get("/asesor/clientes")])
      .then(([m, c]) => { setMe(m.data); setItems(c.data); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const range = periodRange(lf.period, lf.desde, lf.hasta);
  const rows = useMemo(() => items.filter((b) => inPeriod(b.created_at, range)), [items, lf]); // eslint-disable-line react-hooks/exhaustive-deps

  function logout() { advisorToken.clear(); nav("/asesor/login", { replace: true }); }

  return (
    <div className="min-h-screen bg-[#F1F3F3]/40 text-[#3E5641]" data-testid="page-asesor-portal">
      <header className="border-b border-[#3E5641]/10 bg-white">
        <div className="container-x flex h-16 items-center justify-between gap-4">
          <Logo />
          <div className="flex items-center gap-4 text-sm">
            <span className="hidden text-[#6B7678] sm:inline" data-testid="asesor-me">{me ? `${me.nombre} · ${me.agencia || ""}` : ""}</span>
            <button onClick={logout} className="inline-flex items-center gap-1 text-[#6B7678] hover:text-[#3E5641]" data-testid="asesor-logout"><LogOut size={14} /> Salir</button>
          </div>
        </div>
      </header>
      <main className="container-x py-8 md:py-12">
        <p className="mono text-xs uppercase tracking-widest text-[#A0553F]">Portal de asesores · solo consulta</p>
        <h1 className="mt-1 font-serif text-3xl md:text-4xl">Mis clientes</h1>
        <div className="mt-6 mb-4 flex flex-wrap gap-3"><LeadPeriodControls value={lf} onChange={setLf} prefix="asesor" showTipo={false} /></div>
        {!loading && <p className="mb-4 text-sm" data-testid="asesor-counter"><span data-testid="asesor-count">{rows.length}</span> {rows.length === 1 ? "cliente" : "clientes"} en este periodo</p>}
        <AsyncListShell loading={loading} isEmpty={!loading && rows.length === 0} loadingTestId="asesor-loading" emptyTestId="asesor-empty" emptyLabel="No tienes clientes asignados en este periodo.">
          <ClientTable rows={rows} onOpen={(id) => nav(`/asesor/clientes/${id}`)} />
        </AsyncListShell>
      </main>
      {clienteId && <ClientDialog id={clienteId} onClose={() => nav("/asesor")} />}
    </div>
  );
}

function ClientTable({ rows, onOpen }) {
  return (
    <div className="overflow-x-auto rounded-sm border border-[#3E5641]/10 bg-white">
      <table className="w-full text-sm">
        <thead className="bg-[#F1F3F3] text-left text-xs uppercase tracking-wider text-[#6B7678]">
          <tr>{["Ingreso", "Nombre", "Teléfono", "Auto de interés", "Estatus", "Financiera asignada"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-[#3E5641]/10">
          {rows.map((b) => (
            <tr key={b.id} onClick={() => onOpen(b.id)} className="cursor-pointer hover:bg-[#F1F3F3]/60" data-testid={`asesor-row-${b.id}`}>
              <td className="px-4 py-3 text-xs whitespace-nowrap text-[#6B7678]" data-testid={`asesor-ingreso-${b.id}`}>{formatIngreso(b.created_at)}</td>
              <td className="px-4 py-3 font-medium">{b.nombre}</td>
              <td className="px-4 py-3 whitespace-nowrap">{b.telefono}</td>
              <td className="px-4 py-3 text-xs">{autoLabel(b)}</td>
              <td className="px-4 py-3 text-xs">{b.estado}</td>
              <td className="px-4 py-3 text-xs">{b.banco_asignado || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ClientDialog({ id, onClose }) {
  const [b, setB] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    advisorApi.get(`/asesor/clientes/${id}`).then(({ data }) => setB(data)).catch((e) => setError(formatApiError(e)));
  }, [id]);
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-serif text-2xl">{b ? b.nombre : error ? "No disponible" : "Cargando…"}</DialogTitle></DialogHeader>
        {error && <p className="rounded-sm bg-red-50 p-3 text-sm text-red-700" data-testid="asesor-detail-error">{error}</p>}
        {b && <AdvisorClientDetail b={b} />}
      </DialogContent>
    </Dialog>
  );
}
